import { z } from 'zod';
import { Appearance, Persona } from './companions.ts';
import { BuildSource } from './enums.ts';
import { brandedUuid } from './ids.ts';

/**
 * The signup-wizard wire contract — the serializable {@link WizardState} the
 * client threads back each turn and the {@link WizardEvent} it answers with. Both
 * are UNTRUSTED client input, so they are Zod schemas (not erased interfaces): the
 * backend parses them before routing, which is what stops a forged `review` state
 * from skipping `appearance`'s adult-only `AgeRange` validation (compliance red
 * line 1). One source of truth so the backend state machine and the frontend that
 * persists/echoes the state agree on the shape (docs/idea/companions.md §
 * "The AI signup wizard").
 */

/** Where the user is in the scripted flow — the serializable cursor. */
export const WizardStepId = z.enum([
  'choose_source',
  'appearance',
  'upload',
  'persona',
  'review',
  'done',
]);
export type WizardStepId = z.infer<typeof WizardStepId>;

/**
 * The wizard's serializable progress — persisted by the caller and threaded back
 * each turn. Carries the accumulated, already-validated answers plus
 * `girlfriendId` once the draft row exists (the anchor that makes create
 * idempotent on resume). Parsing this rejects a hand-forged state whose fields do
 * not pass the same schemas the forms enforce.
 */
export const WizardState = z.object({
  step: WizardStepId,
  buildSource: BuildSource.optional(),
  appearance: Appearance.optional(),
  persona: Persona.optional(),
  conceptAssetId: brandedUuid('MediaAssetId').optional(),
  girlfriendId: brandedUuid('GirlfriendId').optional(),
});
export type WizardState = z.infer<typeof WizardState>;

/** The user's answer to the last directive (or `start` to open the wizard). */
export const WizardEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('start') }),
  z.object({ type: z.literal('choice'), id: z.string().min(1), value: z.string().min(1).max(200) }),
  z.object({ type: z.literal('form'), id: z.string().min(1), values: z.record(z.string()) }),
  z.object({
    type: z.literal('file'),
    id: z.string().min(1),
    assetId: brandedUuid('MediaAssetId'),
  }),
  z.object({ type: z.literal('action'), action: z.string().min(1).max(200) }),
]);
export type WizardEvent = z.infer<typeof WizardEvent>;
